// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';
import { seriesColor } from '@/theme/seriesColors';
import appFonts from '@/theme/typography';

import { BAR_STRETCH, BAR_TIP_SIZE, TraitBar, TraitBlock } from './TraitParts';
import type { PlayingStyleTrend, TourPlayingStyleEntry } from './types';

type Props = {
  /** The tour's players in leaderboard order. */
  players: { playerId: string; name: string }[];
  styleFor: (playerId: string) => TourPlayingStyleEntry | null;
};

/**
 * Every player of the tour side by side, below the leaderboard: a one-line summary each, then one
 * block per trait with every compared player's bar, in that player's colour, and an arrow on the
 * bar's end for which way it moved with the tour's latest round. Collapsed until asked for — it
 * is long.
 */
export function TourPlayingStylesBoard({ players, styleFor }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  // Coloured by place in the leaderboard, the same per-player palette as the position graph.
  const rows = players.flatMap((player, index) => {
    const style = styleFor(player.playerId);
    return style ? [{ ...player, style, color: seriesColor(index) }] : [];
  });
  const compared = rows.filter((row) => row.style.traits.length > 0);
  const traitKeys = compared[0]?.style.traits.map((trait) => trait.key) ?? [];
  const hasTrends = compared.some((row) => row.style.trends.length > 0);

  const summaryOf = (style: TourPlayingStyleEntry) =>
    style.headline
      ? t(`tourPlayingStyle.trait.${style.headline.key}.${style.headline.level}`)
      : style.traits.length > 0
        ? t('tourPlayingStyle.allAverage')
        : t('tourPlayingStyle.notEnoughHoles');

  return (
    <View style={styles.board} testID="tour-playing-styles-board">
      <Pressable
        style={styles.header}
        onPress={() => setOpen((value) => !value)}
        accessibilityRole="button"
        aria-expanded={open}
        testID="tour-playing-styles-toggle"
      >
        <Text style={styles.title}>{t('tourPlayingStyle.boardTitle')}</Text>
        {/* eslint-disable-next-line i18n/no-literal-text -- disclosure glyph; the state is aria-expanded */}
        <Text style={styles.chevron}>{open ? '▾' : '▸'}</Text>
      </Pressable>

      {open ? (
        <View style={styles.body}>
          <Text style={styles.muted}>{t('tourPlayingStyle.boardHelp')}</Text>

          <View style={styles.summaries}>
            {rows.map((row) => (
              <View key={row.playerId} style={styles.summaryRow} testID={`tour-playing-styles-summary-${row.playerId}`}>
                <Text style={styles.summaryName} numberOfLines={1}>
                  {row.name}
                </Text>
                <Text style={styles.summaryText}>{summaryOf(row.style)}</Text>
              </View>
            ))}
          </View>

          {hasTrends ? <Text style={styles.muted}>{t('tourPlayingStyle.trendLegend')}</Text> : null}

          {traitKeys.map((key) => (
            <TraitBlock key={key} traitKey={key} testIDPrefix="tour-playing-styles">
              {compared.map((row) => {
                const trait = row.style.traits.find((candidate) => candidate.key === key);
                if (!trait) return null;
                const trend = row.style.trends.find((candidate) => candidate.key === key);
                return (
                  <View key={row.playerId} style={styles.barRow} testID={`tour-playing-styles-row-${key}-${row.playerId}`}>
                    <Text style={styles.barName} numberOfLines={1}>
                      {row.name}
                    </Text>
                    <View style={styles.bar}>
                      <TraitBar
                        value={trait.value}
                        stretch={BAR_STRETCH.tour}
                        color={row.color}
                        tip={trend ? <TrendArrow trend={trend} testID={`tour-playing-styles-trend-${key}-${row.playerId}`} /> : null}
                        testID={`tour-playing-styles-bar-${key}-${row.playerId}`}
                      />
                    </View>
                  </View>
                );
              })}
            </TraitBlock>
          ))}
        </View>
      ) : null}
    </View>
  );
}

/**
 * ↑ the bar grew, ↓ it shrank with the latest round; green when that is better golf, red when worse,
 * plain for a trait that is neither. Sits on the end of the bar, in a disc of the card's colour so
 * it reads over the fill, the track and the middle line alike.
 */
function TrendArrow({ trend, testID }: { trend: PlayingStyleTrend; testID: string }) {
  const { t } = useI18n();

  const color =
    trend.verdict === 'better'
      ? appColors.accent.primary
      : trend.verdict === 'worse'
        ? appColors.status.errorText
        : appColors.text.muted;

  return (
    <View style={styles.arrowBadge} testID={testID} accessibilityLabel={t(`tourPlayingStyle.trend.${trend.verdict ?? trend.direction}`)}>
      {/* eslint-disable-next-line i18n/no-literal-text -- direction glyph, named by accessibilityLabel */}
      <Text style={[styles.arrow, { color }]}>{trend.direction === 'up' ? '↑' : '↓'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  board: {
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '800',
    fontFamily: appFonts.brand,
  },
  chevron: {
    color: appColors.text.secondary,
    fontSize: 16,
  },
  body: {
    gap: 14,
  },
  muted: {
    fontSize: 12.5,
    lineHeight: 18,
    color: appColors.text.muted,
  },
  summaries: {
    gap: 6,
  },
  summaryRow: {
    gap: 1,
  },
  summaryName: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.secondary,
  },
  summaryText: {
    fontSize: 13,
    lineHeight: 18,
    color: appColors.text.heading,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  barName: {
    width: 84,
    fontSize: 12.5,
    color: appColors.text.secondary,
  },
  bar: {
    flex: 1,
    // Room for the half of the arrow that overhangs a full bar.
    paddingRight: BAR_TIP_SIZE / 2,
  },
  arrowBadge: {
    width: BAR_TIP_SIZE,
    height: BAR_TIP_SIZE,
    borderRadius: BAR_TIP_SIZE / 2,
    backgroundColor: appColors.background.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrow: {
    fontSize: 12,
    lineHeight: BAR_TIP_SIZE,
    fontWeight: '800',
  },
});
